import type { EmbedReference } from '@shared/ast/astTypes'
import type { CreateEmbedRangeInput, EmbedRangeSelection } from '@shared/ast/embedRanges'
import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import toast from 'react-hot-toast'

import type { ChapterSectionDependency } from '../../../shared/chapterSectionDependencies'
import type { AllDiffsResponse, ChangeMapResponse, HistoryTimelineResponse } from '../../../shared/contentHistory'
import type { TagDefinition, TagRegistry } from '../../../shared/tags/tagRegistry'
import { ContentAST } from '../transforms/ast'
import { Annotation } from '../types/annotations'
import { logger } from '../utils/logger'

// API Configuration
export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001'

// Types
export interface ResolvedEmbed {
  status: 'resolved' | 'missing' | 'ambiguous' | 'cycle'
  source: EmbedReference
  title: string
  content?: ContentAST
  message?: string
  scopeOptions?: Array<{ label: string; source: EmbedReference }>
  range?: EmbedRangeSelection
}

export interface User {
  id: string
  email: string
  username?: string
  role: string
  tier: string
  isEmailVerified: boolean
  firstName?: string
  lastName?: string
  characterTagId?: string
}

/** Author/admin directory row from GET /api/users */
export interface UserDirectoryItem {
  id: string
  email: string
  firstName?: string
  lastName?: string
  role: string
  tier: string
  isActive: boolean
  isEmailVerified: boolean
  characterTagId?: string
  invitedVia?: { id: string; recipientName?: string; status?: string }
  createdAt: string
}

export interface InvitationPublic {
  type: 'regular' | 'character'
  recipientName: string
  characterName?: string
  bookId?: string
  characterTagId?: string
  hasPersonalMessage: boolean
  status: 'pending' | 'claimed' | 'expired'
}

export interface InvitationConfirmResponse {
  personalMessage?: string
  characterName?: string
}

export interface InvitationListItem {
  id: string
  token: string
  type: 'regular' | 'character'
  recipientName: string
  characterTagId?: string
  characterName?: string
  hasPersonalMessage: boolean
  status: 'pending' | 'claimed' | 'expired'
  claimedBy?: { email: string; firstName?: string; lastName?: string }
  claimedAt?: string
  expiresAt?: string
  createdAt: string
}

export interface CharacterDetail {
  tagId: string
  name: string
  walletAddress?: string
  /** The author's words about this character, shown on their page. */
  authorNote?: string
  claimed: boolean
  /** True only for the person holding this character. */
  isMine: boolean
  /**
   * The wallet's twelve-word recovery phrase. Sent only to the author and to the person
   * holding this character — it is the money, and anyone who has it can spend it.
   */
  mnemonic?: string
  /** Total USDC ever sent to this character. Null when the indexer was unreachable. */
  receivedUsdc?: number | null
}

export interface CreateInvitationData {
  type: 'regular' | 'character'
  /** Required for character invitations: where the claimed character lands and binds. */
  bookId?: string
  recipientName: string
  characterTagId?: string
  characterName?: string
  personalMessage?: string
  expiresAt?: string
}

export interface CreatedInvitation {
  id: string
  token: string
  type: string
  recipientName: string
  characterName?: string
  status: string
  createdAt: string
}

// TEMPORARY public-registration window — see server services/openRegistration.ts
export interface PublicRegistrationStatus {
  open: boolean
  openedAt: number | null
}

export interface AuthResponse {
  success: boolean
  message: string
  data: {
    token: string
    user: User
  }
}

export interface Book {
  _id: string
  title: string
  author: {
    _id: string
    username: string
    email: string
  }
  status: 'draft' | 'in_progress' | 'review' | 'published' | 'archived'
  visibility: 'private' | 'public' | 'paid'
  metadata: {
    language: string
    direction: 'ltr' | 'rtl'
    lastModified: Date
    version: string
    summary?: string
  }
  settings: {
    autoSave: boolean
    defaultFontFamily?: string
    defaultFontSize?: string
    titleFontFamily?: string
    /**
     * Book structure: 'chapters' uses the chapter grouping; 'flat' renders
     * sections in `flatSectionsOrder` with no chapter labels. Defaults to 'chapters'.
     */
    structure?: 'chapters' | 'flat'
    chapters?: Array<{ id: string; title: string; chapterSections: string[]; note?: string }>
    /** Ordered ChapterSection keys (`filename:headingBlockId`) for flat structure. */
    flatSectionsOrder?: string[]
    chapterSectionDependencies?: ChapterSectionDependency[]
    /** Per-element ordered ChapterSection keys, keyed by element tag id. */
    elementSectionOrder?: Record<string, string[]>
    /** Sentence per locked passage, keyed by encryption id. Author-only; never sent to readers. */
    lockPhrases?: Record<string, string>
    /** Key for character-passage glyphs. Author-only; never published. */
    withheldGlyphKey?: string
  }
  /**
   * Where the server resolved this book's content from on the current request.
   * 'fs' — an active content session, reading from disk (`_order.json`, content files).
   * 'db' — normal path, reading from Mongo.
   * Author-only UI indicator; readers can ignore.
   */
  contentSource?: 'fs' | 'db'
  createdAt: Date
  updatedAt: Date
}

export interface ContentFile {
  /** Stable filename without extension, e.g. "פתחתי-את-הדלת" */
  filename: string
  /** Slug identifier (same as filename) */
  slug: string
  title: string
  order: number
  published: boolean
  status: 'draft' | 'in_progress' | 'review' | 'published'
  /** Approximate token count */
  tokenCount?: number
  /** Author view — full AST */
  authorContent?: ContentAST
  /** Reader view — generated on-the-fly by server (encrypted glyphs, optional translation) */
  content?: ContentAST
  /** Reader TOC entries for `^`-marked chapter sections (see GET chapter?view=reader) */
  readerTocSections?: Array<{ blockId: string; displayText: string }>
  metadata: {
    summary?: string
    notes?: string
    version: string
  }
  /** Which chapter group this section belongs to (reader view only, from /published). */
  chapterId?: string
  /** For reader-view entries, the ^-marked heading blockId that identifies this section within its source file. */
  sectionId?: string
  /**
   * Reader-only flag (flat structure): true when this entry is the last item in
   * `flatSectionsOrder` and the entry that precedes it in the flat order is not
   * yet published. The reader renders a `…` gap indicator immediately before it.
   */
  hasGapBefore?: boolean
  /** HeadingBlockIds of `^`-marked sections that are currently published from this source file. */
  publishedSections?: string[]
  encryptedSections?: {
    sectionId: string
    contentHash: string
    lastModified: string
  }[]
  annotations: Annotation[]
  fileTags?: string[]
  revision?: string
}

export interface TheLastManifestoReaderBundle {
  book: Book
  chapters: ContentFile[]
  languages: { available: string[]; source: string }
  /** Characters who put something on their own page, and so asked to be listed. */
  characters: Array<{ tagId: string; name: string }>
}

export interface CreateBookData {
  title: string
  language: string
  visibility?: 'private' | 'public' | 'paid'
}

export interface UpdateBookData {
  title?: string
  language?: string
  status?: 'draft' | 'in_progress' | 'review' | 'published' | 'archived'
  visibility?: 'private' | 'public' | 'paid'
  settings?: {
    autoSave?: boolean
    defaultFontFamily?: string
    defaultFontSize?: string
    titleFontFamily?: string
    structure?: 'chapters' | 'flat'
    chapters?: Array<{ id: string; title: string; chapterSections: string[]; note?: string }>
    flatSectionsOrder?: string[]
    chapterSectionDependencies?: ChapterSectionDependency[]
    elementSectionOrder?: Record<string, string[]>
    /** Sentence per locked passage, keyed by encryption id. Author-only; never sent to readers. */
    lockPhrases?: Record<string, string>
    /** Key for character-passage glyphs. Author-only; never published. */
    withheldGlyphKey?: string
  }
}

export interface CreateChapterData {
  title: string
  authorContent?: ContentAST
  order?: number
}

export interface UpdateChapterData {
  title?: string
  authorContent?: ContentAST
  status?: 'draft' | 'in_progress' | 'review' | 'published'
  published?: boolean
  order?: number
  metadata?: {
    summary?: string
    notes?: string
    version?: string
  }
  encryptedSections?: {
    sectionId: string
    contentHash: string
    lastModified: string
  }[]
  annotations?: Annotation[]
  fileTags?: string[]
}

/**
 * What a chapter update returns by default: file metadata only. The full
 * document (AST + sidecar + annotations) is echoed back only when the caller
 * passes `returnFull`, because on the largest source files that response is
 * megabytes and the autosave loop throws it away.
 */
export interface ContentFileAck {
  filename: string
  slug: string
  title: string
  order: number
  published: boolean
  status: string
  fileTags?: string[]
  publishedSections?: string[]
  /** Revision of the file as just written — the client's new cache baseline. */
  revision: string
}

export interface SearchResult {
  filename: string
  chapterTitle: string
  chapterOrder: number
  blockId: string
  blockType: string
  headingPath: string[]
  snippet: string
  matchStart: number
  matchEnd: number
  blockMatchStart: number
  blockMatchEnd: number
  tagIds?: string[]
  annotationType?: 'file' | 'block' | 'blockSpan' | 'inlineRange' | 'heading'
}

export interface ApiError {
  success: false
  message: string
  errors?: string[]
}

export interface ApiResponse<T> {
  success: boolean
  message?: string
  data: T
  count?: number
}

/**
 * Below this, gzip's header plus the CPU round-trip costs more than it saves.
 * Above it we are into whole-AST payloads, which is the case that matters: a
 * chapter autosave fires every 3s while typing and carries the entire source
 * file — over a megabyte of JSON for the largest one.
 */
const GZIP_REQUEST_MIN_BYTES = 32 * 1024

/**
 * Compress JSON request bodies. Browsers gzip responses automatically but never
 * request bodies, so uploads run uncompressed unless we do it here. No server
 * counterpart is needed: `express.json()` inflates `Content-Encoding: gzip`
 * bodies natively.
 *
 * Only plain JSON payloads are touched — anything the browser owns the framing
 * of (FormData, Blob, typed arrays) must reach the server byte-for-byte.
 */
async function compressRequestBody(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
  const data = config.data
  if (
    typeof CompressionStream === 'undefined' ||
    data == null ||
    typeof data !== 'object' ||
    data instanceof FormData ||
    data instanceof Blob ||
    data instanceof ArrayBuffer ||
    data instanceof URLSearchParams ||
    ArrayBuffer.isView(data)
  ) {
    return config
  }

  const bytes = new TextEncoder().encode(JSON.stringify(data))
  if (bytes.byteLength < GZIP_REQUEST_MIN_BYTES) {
    return config
  }

  const compressed = new Blob([bytes]).stream().pipeThrough(new CompressionStream('gzip'))
  config.data = new Uint8Array(await new Response(compressed).arrayBuffer())
  config.headers.set('Content-Type', 'application/json')
  config.headers.set('Content-Encoding', 'gzip')
  return config
}

class ApiService {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 120000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Enable cookies for refresh tokens
    })

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return compressRequestBody(config)
      },
      (error) => {
        return Promise.reject(error)
      },
    )

    // Response interceptor for error handling and token refresh
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response
      },
      async (error) => {
        const originalRequest = error.config

        // If token expired and we haven't already tried to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true

          try {
            // Try to refresh the token
            const refreshResponse = await this.refreshToken()
            if (refreshResponse.success) {
              // Update stored token
              localStorage.setItem('token', refreshResponse.data.token)
              // Update user data if provided
              if (refreshResponse.data.user) {
                localStorage.setItem('user', JSON.stringify(refreshResponse.data.user))
              }
              // Retry original request with new token
              originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.token}`
              return this.api(originalRequest)
            }
          } catch (refreshError) {
            // Refresh failed - redirect to login
            localStorage.removeItem('token')
            localStorage.removeItem('user')
            window.location.href = '/login'
            return Promise.reject(refreshError)
          }
        } else if (error.response?.status === 401) {
          // Already tried refresh or other 401 error - redirect to login
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          window.location.href = '/login'
        }

        // Show global API toasts only when explicitly requested by the caller.
        // Most screens handle errors locally and should avoid duplicate notifications.
        const shouldShowGlobalToast = Boolean((error.config as any)?.notifyOnError)
        const isAuthEndpoint = error.config?.url?.includes('/api/auth/')
        if (shouldShowGlobalToast && !isAuthEndpoint) {
          const message = error.response?.data?.message || 'An error occurred'
          toast.error(message)
        }

        return Promise.reject(error)
      },
    )
  }

  // Health check
  async healthCheck() {
    const response = await this.api.get('/health')
    return response.data
  }

  // Authentication endpoints
  async register(userData: {
    email: string
    password: string
    firstName?: string
    invitationToken?: string
  }): Promise<AuthResponse> {
    try {
      const response = await this.api.post('/api/auth/register', userData)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Registration failed' }
    }
  }

  async login(credentials: { email: string; password: string; rememberMe?: boolean }): Promise<AuthResponse> {
    try {
      const response = await this.api.post('/api/auth/login', credentials)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Login failed' }
    }
  }

  async logout(): Promise<void> {
    try {
      await this.api.post('/api/auth/logout')
    } catch (error) {
      logger.error('Logout error:', error)
    } finally {
      // Always clear local storage
      localStorage.removeItem('token')
      localStorage.removeItem('user')
    }
  }

  async refreshToken(): Promise<AuthResponse> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/refresh-token`,
        {},
        {
          withCredentials: true,
        },
      )
      return response.data
    } catch (error: any) {
      throw error.response?.data || { message: 'Token refresh failed' }
    }
  }

  // User endpoints
  async getCurrentUser(): Promise<User> {
    try {
      const response = await this.api.get('/api/auth/me')
      return response.data.data.user
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to get user' }
    }
  }

  async updateProfile(userData: Partial<User>): Promise<User> {
    try {
      const response = await this.api.put('/api/users/profile', userData)
      return response.data.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to update profile' }
    }
  }

  // Books endpoints
  async getBooks(): Promise<ApiResponse<Book[]>> {
    try {
      const response = await this.api.get('/api/books')
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to get books' }
    }
  }

  async getBook(bookId: string): Promise<ApiResponse<Book>> {
    try {
      const response = await this.api.get(`/api/books/${bookId}`)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to get book' }
    }
  }

  async createBook(bookData: CreateBookData): Promise<ApiResponse<Book>> {
    try {
      const response = await this.api.post('/api/books', bookData)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to create book' }
    }
  }

  async updateBook(bookId: string, bookData: UpdateBookData): Promise<ApiResponse<Book>> {
    try {
      const response = await this.api.put(`/api/books/${bookId}`, bookData)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to update book' }
    }
  }

  async deleteBook(bookId: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.api.delete(`/api/books/${bookId}`)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to delete book' }
    }
  }

  async uploadBookImage(
    bookId: string,
    file: File,
  ): Promise<ApiResponse<{ url: string; filename: string; size: number }>> {
    try {
      const formData = new FormData()
      formData.append('image', file)

      const response = await this.api.post(`/api/books/${bookId}/images`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to upload image' }
    }
  }

  // Chapters endpoints
  async getChapters(bookId: string, lang?: string): Promise<ApiResponse<ContentFile[]>> {
    try {
      const params = lang ? { lang } : undefined
      const response = await this.api.get(`/api/chapters/${bookId}`, { params })
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to get chapters' }
    }
  }

  /** Canonical naming for chapter sections sidebar data. */
  async getChapterSectionsToc(bookId: string): Promise<
    ApiResponse<
      Array<{
        filename: string
        headingBlockId: string
        headingText: string
        displayText: string
        ancestorLabels?: string[]
        tagIds?: string[]
        /** Published, but contains an encrypted passage with no glyphs — readers see a hole. */
        needsRepublish?: boolean
      }>
    >
  > {
    try {
      const response = await this.api.get(`/api/chapters/${bookId}/chapter-sections-toc`)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to get chapter sections TOC' }
    }
  }

  /**
   * Publish a ^-marked chapter section. Optionally merges client-encrypted glyphs
   * into the source file's encryptedSections. Server updates source.publishedSections
   * and source.encryptedSections in a single operation.
   */
  async publishChapterFromMarkedSection(
    bookId: string,
    body: {
      sourceFilename: string
      headingBlockId: string
      encryptedSections?: Array<{
        sectionId: string
        contentHash: string
        lastModified: string
        encryptedGlyphs: string[]
      }>
    },
  ): Promise<
    ApiResponse<{
      sourceFilename: string
      headingBlockId: string
      publishedSections: string[]
      encryptedSectionIds: string[]
    }>
  > {
    try {
      const response = await this.api.post(`/api/chapters/${bookId}/publish-chapter`, body)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to publish chapter from marked section' }
    }
  }

  /** Unpublish a ^-marked chapter section: removes headingBlockId from source.publishedSections. */
  async unpublishChapterSection(
    bookId: string,
    body: { sourceFilename: string; headingBlockId: string },
  ): Promise<
    ApiResponse<{
      sourceFilename: string
      headingBlockId: string
      publishedSections: string[]
      published: boolean
    }>
  > {
    try {
      const response = await this.api.post(`/api/chapters/${bookId}/unpublish-section`, body)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to unpublish chapter section' }
    }
  }

  async resolveEmbed(bookId: string, source: EmbedReference): Promise<ApiResponse<ResolvedEmbed>> {
    try {
      const response = await this.api.get(`/api/chapters/${bookId}/embeds/resolve`, { params: source })
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to resolve embedded source' }
    }
  }

  async createEmbedRange(
    bookId: string,
    input: CreateEmbedRangeInput,
  ): Promise<ApiResponse<{ source: EmbedReference; revision: string }>> {
    try {
      const response = await this.api.post(`/api/chapters/${bookId}/embeds/ranges`, input)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to create embedded range' }
    }
  }

  async getChapter(bookId: string, chapterId: string): Promise<ApiResponse<ContentFile>> {
    try {
      const response = await this.api.get(`/api/chapters/${bookId}/${chapterId}`)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to get chapter' }
    }
  }

  /**
   * Just the file's current revision — the freshness check for a cached chapter.
   * Fetching the whole file to compare would cost up to megabytes; this is a
   * few hundred bytes, so the full download only happens when it has to.
   */
  async getChapterRevision(
    bookId: string,
    chapterId: string,
  ): Promise<ApiResponse<{ filename: string; revision: string }>> {
    try {
      const response = await this.api.get(`/api/chapters/${bookId}/${chapterId}/revision`)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to get chapter revision' }
    }
  }

  async createChapter(bookId: string, chapterData: CreateChapterData): Promise<ApiResponse<ContentFile>> {
    try {
      const response = await this.api.post(`/api/chapters/${bookId}`, chapterData)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to create chapter' }
    }
  }

  async updateChapter(
    bookId: string,
    chapterId: string,
    chapterData: UpdateChapterData,
    options: { returnFull: true },
  ): Promise<ApiResponse<ContentFile>>
  async updateChapter(
    bookId: string,
    chapterId: string,
    chapterData: UpdateChapterData,
    options?: { returnFull?: false },
  ): Promise<ApiResponse<ContentFileAck>>
  async updateChapter(
    bookId: string,
    chapterId: string,
    chapterData: UpdateChapterData,
    options?: { returnFull?: boolean },
  ): Promise<ApiResponse<ContentFile | ContentFileAck>> {
    try {
      const query = options?.returnFull ? '?returnFull=1' : ''
      const response = await this.api.put(`/api/chapters/${bookId}/${chapterId}${query}`, chapterData)
      if (response.data.success && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('manifesto:content-saved', { detail: { bookId, filename: chapterId } }))
      }
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to update chapter' }
    }
  }

  async renameSourceFile(bookId: string, oldFilename: string, newTitle: string): Promise<ApiResponse<ContentFile>> {
    try {
      const response = await this.api.post(`/api/chapters/${bookId}/${oldFilename}/rename`, { newTitle })
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to rename source file' }
    }
  }

  async deleteChapter(bookId: string, chapterId: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.api.delete(`/api/chapters/${bookId}/${chapterId}`)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to delete chapter' }
    }
  }

  async reorderChapters(
    bookId: string,
    chapterOrders: { filename: string; order: number }[],
  ): Promise<ApiResponse<ContentFile[]>> {
    try {
      const response = await this.api.put(`/api/chapters/${bookId}/reorder`, { chapterOrders })
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to reorder chapters' }
    }
  }

  async searchChapters(bookId: string, query?: string, tags?: string[]): Promise<ApiResponse<SearchResult[]>> {
    try {
      const params: Record<string, string> = {}
      if (query) {
        params.q = query
      }
      if (tags && tags.length > 0) {
        params.tags = tags.join(',')
      }
      const response = await this.api.get(`/api/chapters/${bookId}/search`, { params })
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to search chapters' }
    }
  }

  // Annotation API methods
  async getAnnotations(bookId: string, chapterId: string): Promise<ApiResponse<Annotation[]>> {
    try {
      const response = await this.api.get(`/api/chapters/${bookId}/${chapterId}/annotations`)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to get annotations' }
    }
  }

  async updateAnnotation(
    bookId: string,
    chapterId: string,
    annotation: Annotation,
  ): Promise<ApiResponse<Annotation[]>> {
    try {
      const response = await this.api.put(`/api/chapters/${bookId}/${chapterId}/annotations`, {
        id: annotation.id,
        tags: annotation.tags,
        anchor: annotation.anchor,
      })
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to update annotation' }
    }
  }

  async deleteAnnotation(bookId: string, chapterId: string, annotationId: string): Promise<ApiResponse<Annotation[]>> {
    try {
      const response = await this.api.delete(`/api/chapters/${bookId}/${chapterId}/annotations/${annotationId}`)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to delete annotation' }
    }
  }

  async bulkUpdateAnnotations(
    bookId: string,
    chapterId: string,
    annotations: Annotation[],
  ): Promise<ApiResponse<Annotation[]>> {
    try {
      const response = await this.api.put(`/api/chapters/${bookId}/${chapterId}/annotations/bulk`, { annotations })
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to update annotations' }
    }
  }

  // AST-specific methods

  async createChapterAST(
    bookId: string,
    chapterData: CreateChapterData & { authorContent: ContentAST },
  ): Promise<ApiResponse<ContentFile>> {
    try {
      const response = await this.api.post(`/api/chapters/${bookId}`, {
        ...chapterData,
        contentFormat: 'ast',
      })
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to create chapter with AST' }
    }
  }

  async updateChapterAST(
    bookId: string,
    chapterId: string,
    chapterData: UpdateChapterData & { authorContent?: ContentAST },
  ): Promise<ApiResponse<ContentFile>> {
    try {
      const response = await this.api.put(`/api/chapters/${bookId}/${chapterId}`, {
        ...chapterData,
      })
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to update chapter with AST' }
    }
  }

  async listCharacters(
    bookId: string,
  ): Promise<{ tagId: string; name: string; walletAddress?: string; authorNote?: string; claimed: boolean }[]> {
    try {
      const response = await this.api.get(`/api/books/${bookId}/characters`)
      return response.data.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to list characters' }
    }
  }

  /** The author's words about a character, shown to readers on that character's page. */
  async saveCharacterNote(bookId: string, tagId: string, note: string): Promise<void> {
    try {
      await this.api.put(`/api/books/${bookId}/characters/${tagId}/note`, { note })
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to save the note' }
    }
  }

  /** Add a character by name; the server creates its tag under the characters root. */
  async createCharacter(bookId: string, name: string): Promise<{ tagId: string; name: string }> {
    try {
      const response = await this.api.post(`/api/books/${bookId}/characters`, { name })
      return response.data.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to create character' }
    }
  }

  async setCharacterConsent(bookId: string, filename: string, encryptionId: string, action: 'release'): Promise<void> {
    try {
      await this.api.post(`/api/chapters/${bookId}/${encodeURIComponent(filename)}/consent`, {
        encryptionId,
        action,
      })
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to record your decision' }
    }
  }

  async saveLockPhrase(bookId: string, encryptionId: string, phrase: string): Promise<void> {
    try {
      await this.api.put(`/api/books/${bookId}/lock-phrase`, { encryptionId, phrase })
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to save the lock phrase' }
    }
  }

  async getCharacter(bookId: string, tagId: string): Promise<CharacterDetail> {
    try {
      const response = await this.api.get(`/api/books/${bookId}/characters/${tagId}`)
      return response.data.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to load character' }
    }
  }

  // ===== Public Book Access (no auth required - token added automatically if exists) =====
  async getPublicBooksList(): Promise<ApiResponse<Book[]>> {
    try {
      const response = await this.api.get('/api/books/public/list')
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to load public books' }
    }
  }

  async getBookLanguages(bookId: string): Promise<ApiResponse<{ languages: string[]; sourceLanguage: string }>> {
    try {
      const response = await this.api.get(`/api/books/${bookId}/languages`)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to get book languages' }
    }
  }

  async getTagRegistry(bookId: string): Promise<ApiResponse<TagRegistry>> {
    try {
      const response = await this.api.get(`/api/books/${bookId}/tags`)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to get tag registry' }
    }
  }

  async getTagUsageCounts(bookId: string): Promise<ApiResponse<Record<string, Record<string, number>>>> {
    try {
      const response = await this.api.get(`/api/books/${bookId}/tags/usage`)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to get tag usage counts' }
    }
  }

  async updateTagRegistry(
    bookId: string,
    registry: { version: number; tags: Record<string, TagDefinition> },
  ): Promise<ApiResponse<TagRegistry>> {
    try {
      const response = await this.api.put(`/api/books/${bookId}/tags`, registry)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to update tag registry' }
    }
  }

  // Get The Last Manifesto book (lookup ID, then fetch via reader endpoint)
  async getTheLastManifesto(): Promise<ApiResponse<Book>> {
    try {
      const lookupResponse = await this.api.get('/api/the-last-manifesto')
      const bookId = lookupResponse.data?.data?.bookId
      if (!bookId) {
        throw { response: { data: { success: false, message: 'The Last Manifesto book not found' } } }
      }
      // ?view=reader: even when the caller is the book's author, return the
      // public reader shape (per-section chapter entries) rather than the raw
      // source-file list used by the editor preview.
      const response = await this.api.get(`/api/books/${bookId}/published?view=reader`)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to load The Last Manifesto' }
    }
  }

  /**
   * One-shot reader payload: book metadata, all published section ASTs, language list.
   * Replaces the serial chain of /the-last-manifesto + /books/:id/published + /languages
   * + N × /chapters/:filename?section= with a single request.
   */
  async getTheLastManifestoReader(lang?: string, bookId?: string): Promise<ApiResponse<TheLastManifestoReaderBundle>> {
    try {
      const params = new URLSearchParams()
      if (lang) {
        params.set('lang', lang)
      }
      if (bookId) {
        params.set('bookId', bookId)
      }
      const query = params.toString() ? `?${params.toString()}` : ''
      const response = await this.api.get(`/api/the-last-manifesto/reader${query}`)
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Failed to load The Last Manifesto' }
    }
  }

  // Google Auth
  async googleAuth(credential: string, invitationToken?: string): Promise<AuthResponse> {
    try {
      const response = await this.api.post('/api/auth/google', { credential, invitationToken })
      return response.data
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Google authentication failed' }
    }
  }

  // Invitations
  async getInvitationByToken(token: string): Promise<ApiResponse<InvitationPublic>> {
    const response = await this.api.get(`/api/invitations/by-token/${token}`)
    return response.data
  }

  async confirmInvitation(token: string): Promise<ApiResponse<InvitationConfirmResponse>> {
    const response = await this.api.post(`/api/invitations/by-token/${token}/confirm`)
    return response.data
  }

  async createInvitation(data: CreateInvitationData): Promise<ApiResponse<CreatedInvitation>> {
    const response = await this.api.post('/api/invitations', data)
    return response.data
  }

  async listInvitations(): Promise<ApiResponse<InvitationListItem[]>> {
    const response = await this.api.get('/api/invitations')
    return response.data
  }

  async deleteInvitation(id: string): Promise<ApiResponse<null>> {
    const response = await this.api.delete(`/api/invitations/${id}`)
    return response.data
  }

  async listUsers(): Promise<ApiResponse<UserDirectoryItem[]>> {
    const response = await this.api.get('/api/users')
    return response.data
  }

  async deleteUser(id: string): Promise<ApiResponse<null>> {
    const response = await this.api.delete(`/api/users/${id}`)
    return response.data
  }

  // Content history — reads the sibling content repo's git log. Author-only, and
  // only where that repo is checked out, so callers must honour `available`.
  async getHistoryAvailability(bookId: string): Promise<{ available: boolean; reason?: string }> {
    const response = await this.api.get(`/api/history/${bookId}/availability`)
    return response.data.data
  }

  async getHistoryTimeline(
    bookId: string,
    params: { limit?: number; skip?: number } = {},
  ): Promise<HistoryTimelineResponse> {
    const response = await this.api.get(`/api/history/${bookId}/timeline`, { params })
    return response.data.data
  }

  async getHistoryChanges(bookId: string, to: string, from?: string): Promise<ChangeMapResponse> {
    const response = await this.api.get(`/api/history/${bookId}/changes`, { params: { to, ...(from ? { from } : {}) } })
    return response.data.data
  }

  async getHistoryAllDiffs(bookId: string, to: string, from?: string): Promise<AllDiffsResponse> {
    const response = await this.api.get(`/api/history/${bookId}/diffs`, {
      params: { to, ...(from ? { from } : {}) },
    })
    return response.data.data
  }

  // TEMPORARY public-registration window — see server services/openRegistration.ts
  async getPublicRegistration(): Promise<PublicRegistrationStatus> {
    const response = await this.api.get('/api/open-registration')
    return response.data.data
  }

  async setPublicRegistration(open: boolean): Promise<PublicRegistrationStatus> {
    const response = open
      ? await this.api.post('/api/open-registration')
      : await this.api.delete('/api/open-registration')
    return response.data.data
  }
}

// Export singleton instance
export const apiService = new ApiService()
export default apiService
